import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { cloneObject, manageEventListener } from '../../common/utils';
import { allowedLanguages } from '../../languages';
import withLocalization from '../higherOrderComponents/withLocalization';
import Loader from './Loader';
import { FormikForm, FormikInput, FormikRadioGroup } from './FormikForm';
import { Button } from './ActionElement';
import { allowedInputTypes } from './CustomInput';
import { OnSubmitValidationError } from './OnSubmitValidationError';

const EditableAreaContext = React.createContext();

class EditableArea extends React.Component {
  constructor(props) {
    super(props);
    const { isEditMode = false } = props;

    this.state = {
      isEditMode,
      isToggling: false
    };

    this.receivedFormikProps = null; // TODO: refactor to use FormikForm render prop!!!
    this.manageEventListeners = this.manageEventListeners.bind(this);
    this.handleKeyUpEvent = this.handleKeyUpEvent.bind(this);
    this.toggleAreaEditMode = this.toggleAreaEditMode.bind(this);
    this.renderLabel = this.renderLabel.bind(this);
    this.renderField = this.renderField.bind(this);
    this.getValue = this.getValue.bind(this);
    this.onValidationError = this.onValidationError.bind(this);
    this.refButtonMenuArea = React.createRef();
  }

  componentDidMount() {
    this.manageEventListeners('add');
  }

  componentWillUnmount() {
    this.manageEventListeners('remove');
  }

  onValidationError() {
    const { onValidationError: passedValidationErrorHandler } = this.props;
    const { receivedFormikProps } = this;

    if (!passedValidationErrorHandler) {
      return;
    }

    passedValidationErrorHandler(receivedFormikProps);
  }

  getValue(fieldName) {
    const {
      receivedFormikProps: {
        values // TODO: refactor to use FormikForm render prop!!!
      }
    } = this;

    return values[fieldName];
  }

  handleKeyUpEvent({ key }) {
    if (key === 'Escape') {
      const {
        state: {
          isEditMode
        },
        toggleAreaEditMode
      } = this;

      if (isEditMode) {
        toggleAreaEditMode(false);
      }
    }
  }

  manageEventListeners(action) {
    manageEventListener(document, 'keyup', action, this.handleKeyUpEvent);
  }

  toggleAreaEditMode(forcedEditModeState) {
    const {
      props: {
        translate
      },
      state: {
        isEditMode
      },
      receivedFormikProps: {
        dirty,
        isSubmitting,
        resetForm
      }
    } = this;

    const isLeavingConfirmed = (isEditMode && dirty && !isSubmitting)
      ? window.confirm(translate('messages/prompt-leavingDirtyForm'))
      : true;

    if (isLeavingConfirmed) {
      this.setState({ isToggling: true }, () => {
        resetForm();

        this.setState(({ isEditMode: isStillEditMode }) => ({
          isEditMode: forcedEditModeState || !isStillEditMode,
          isToggling: false
        }));
      });
    }
  }

  renderLabel(relatedFieldName, labelContent) {
    const {
      state: {
        isEditMode
      }
    } = this;

    return !isEditMode ? labelContent : (
      <label htmlFor={`editable-field-${relatedFieldName}`}>
        {labelContent}
      </label>
    );
  }

  renderField(
    fieldName,
    inputDescription,
    inputType = 'text',
    inputProps = {},
    booleanFieldPrints = {
      true: '',
      false: ''
    }
  ) {
    const expectedInputTypes = [
      ...allowedInputTypes,
      'radio-group'
    ];

    if (!expectedInputTypes.includes(inputType)) {
      throw new Error(
        `ERROR: invalid input type passed to renderField method: "${inputType}".
        Expected: ${expectedInputTypes.join(', ')}`
      );
    }

    const {
      props: {
        initialValues,
        formatDateTime
      },
      state: {
        isEditMode
      }
    } = this;

    const fieldValue = initialValues[fieldName];

    if (isEditMode) {
      switch (inputType) {
        case 'radio-group': {
          const {
            radioInputs,
            isSwappedAlign,
            labelledById
          } = inputProps;

          const radioGroupProps = {
            fieldName,
            radioInputs,
            isSwappedAlign,
            initialValue: fieldValue
          };

          if (labelledById) {
            radioGroupProps.labelledById = labelledById;
          }

          else {
            radioGroupProps.title = inputDescription;
          }

          return <FormikRadioGroup {...radioGroupProps} />;
        }

        default:
          return (
            <FormikInput
              {...inputProps}
              name={fieldName}
              id={`editable-field-${fieldName}`}
              type={inputType}
              description={inputDescription}
            />
          );
      }
    }

    switch (inputType) {
      case 'select': {
        return !Array.isArray(fieldValue) ? fieldValue : fieldValue.join(', ');
      }
      case 'select-async': {
        return !Array.isArray(fieldValue) ? fieldValue : fieldValue.join(', ');
      }

      case 'radio-group': {
        const optedRadioInput = inputProps.radioInputs.find(({ value }) => value === fieldValue);
        const {
          label,
          description
        } = optedRadioInput;

        return typeof (label) === 'string' ? label : description;
      }

      case 'date':
        return !fieldValue || fieldValue === '' ? '' : formatDateTime(
          fieldValue,
          {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }
        );

      case 'address':
        return fieldValue.address;

      case 'checkbox':
        return booleanFieldPrints[fieldValue ? 'true' : 'false'];

      default:
        return fieldValue;
    }
  }

  render() {
    const {
      props,
      state: {
        isEditMode,
        isToggling
      },
      toggleAreaEditMode,
      renderLabel,
      renderField,
      getValue,
      refButtonMenuArea
    } = this;

    const {
      user,
      translate,
      ariaEditorName,
      isLoading,
      loadingText,
      savingText,
      onSubmit: passedSubmitHandler,
      buttonStyleType,
      displayButtonLabels,
      children
    } = props;

    const isLoggedIn = !!user;
    const isEditorProcessing = isLoading || isToggling;
    const sharedIconAttributes = { style: { color: 'inherit' } };

    const editorButtonAreaProps = {
      ref: refButtonMenuArea,
      className: `editor-control mode-${isEditMode ? 'edit' : 'print'}`
    };

    const addedFormProps = {
      formClassNames: [
        'editable-area',
        `mode-${isEditMode ? 'edit' : 'print'}`
      ],
      getFormikProps: receivedFormikProps => {
        this.receivedFormikProps = receivedFormikProps; // TODO: refactor to use FormikForm render prop!!!
      }
    };

    const excludedFormProps = [
      'language',
      'children',
      'isEditMode',
      'ariaEditorName',
      'user'
    ];

    const passedFormikProps = cloneObject(props, addedFormProps, excludedFormProps);
    passedFormikProps.onSubmit = values => passedSubmitHandler({ values, toggleAreaEditMode });

    return (
      <EditableAreaContext.Provider value={{
        isEditMode,
        toggleAreaEditMode,
        renderLabel,
        renderField,
        getValue
      }}
      >
        <Loader hasLoaded={!isLoading} loadingText={isEditMode ? savingText : loadingText}>
          <FormikForm {...passedFormikProps}>
            <OnSubmitValidationError callback={this.onValidationError} />
            <EditableAreaContext.Consumer>
              {children}
            </EditableAreaContext.Consumer>

            {!isLoggedIn ? null : (
              <div {...editorButtonAreaProps}>
                <Button
                  className={buttonStyleType !== 'wrap' ? 'itemgap-right' : null}
                  type='submit'
                  appearance={buttonStyleType}
                  title={translate('editableArea/button-submit-title')}
                  disabled={isEditorProcessing || !isEditMode}
                  iconName='check'
                  iconAttributes={sharedIconAttributes}
                >
                  {displayButtonLabels ? translate('editableArea/button-submit') : null}
                </Button>

                <Button
                  onClick={() => toggleAreaEditMode()}
                  buttonColor='secondary'
                  className='button-toggler'
                  appearance={buttonStyleType}
                  iconName={(isEditMode) ? 'times' : 'edit'}
                  title={
                    translate(`editableArea/button-${isEditMode ? 'closer' : 'edit'}-title`, { ariaEditorName })
                  }
                  aria-expanded={isEditMode || isToggling}
                  disabled={isEditorProcessing}
                  iconAttributes={sharedIconAttributes}
                >
                  {displayButtonLabels ? translate(`editableArea/button-${isEditMode ? 'closer' : 'edit'}`) : null}
                </Button>
              </div>
            )}
          </FormikForm>
        </Loader>
      </EditableAreaContext.Provider>
    );
  }
}

EditableArea.propTypes = {
  language: PropTypes.oneOf(allowedLanguages).isRequired,
  user: PropTypes.object,
  initialValues: PropTypes.object.isRequired,
  handleDirtyForm: PropTypes.func,
  validateOnBlur: PropTypes.bool,
  validateOnChange: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  validationSchema: PropTypes.object,
  formClassNames: PropTypes.arrayOf(PropTypes.string),
  savingText: PropTypes.string,
  loadingText: PropTypes.string,
  ariaEditorName: PropTypes.string.isRequired,
  buttonStyleType: PropTypes.oneOf(['wrap', 'outline']),
  isEditMode: PropTypes.bool,
  isLoading: PropTypes.bool,
  displayButtonLabels: PropTypes.bool,
  onValidationError: PropTypes.func
};

export default withLocalization(connect(({ user }) => ({ user }))(EditableArea), [
  'elements/editableArea',
  'general/messages'
]);